import type React from "react";
import {useEffect, useState} from "react";
import {Autocomplete, CircularProgress, Stack, TextField, Typography} from "@mui/material";
import {COLORS} from "~/style/colors-ts";
import {supplierNameOptionKey, type SupplierNameOption} from "./supplierNameOption";

const SEARCH_DEBOUNCE_MS = 500;

type SupplierNamePickerProps = {
    /** Candidates to show. The caller runs its own query — the two call sites use different endpoints. */
    options: SupplierNameOption[];
    loading: boolean;
    /** The typed text, debounced. Feed it to the caller's own query. */
    onSearch: (term: string) => void;
    /** Open/closed, so the caller can hold its query back until the dropdown is actually showing. */
    onOpenChange?: (open: boolean) => void;
    /** Text in the input. Owned by the caller so it can reset on save or cancel. */
    inputValue: string;
    onInputChange: (value: string) => void;
    onSelect: (option: SupplierNameOption) => void;
    /**
     * Adds the `+ Add "…"` row (and `freeSolo`) for a name no candidate matches. The Identity
     * section's Parent field wants it, since a brand-new parent is just free text on the
     * relationship; a picker that can only reference something that already exists does not.
     */
    allowAdd?: boolean;
    disabled?: boolean;
    /** Shows the spinner for a caller-side write that is still in flight. */
    busy?: boolean;
    label?: string;
    placeholder?: string;
    emptyText?: string;
    autoFocus?: boolean;
    variant?: 'outlined' | 'standard';
    onEscape?: () => void;
    onBlur?: () => void;
};

/**
 * Shared supplier-name typeahead: the Identity section's Parent field and the Cluster table's
 * new-row picker.
 *
 * It owns the Autocomplete wiring, the open state and the search debounce; the **query** belongs
 * to the caller, because the two want different data — Parent searches `relationship/parent_names/`
 * (§2.4), while the Cluster picker searches raw rows across the databag via
 * `cluster_member/?databag_id=&search=` (§2.2).
 */
export const SupplierNamePicker: React.FC<SupplierNamePickerProps> = ({
                                                                          options,
                                                                          loading,
                                                                          onSearch,
                                                                          onOpenChange,
                                                                          inputValue,
                                                                          onInputChange,
                                                                          onSelect,
                                                                          allowAdd = false,
                                                                          disabled = false,
                                                                          busy = false,
                                                                          label,
                                                                          placeholder,
                                                                          emptyText = 'No match',
                                                                          autoFocus = false,
                                                                          variant = 'outlined',
                                                                          onEscape,
                                                                          onBlur,
                                                                      }) => {
    const [open, setOpen] = useState(false);

    // Debounced off the caller's own input value, so neither call site repeats the timer plumbing.
    useEffect(() => {
        const timer = setTimeout(() => onSearch(inputValue.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // `onSearch` is re-created per render at both call sites; keying on the text alone is what
        // keeps this to one timer per keystroke rather than one per render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]);

    const setOpenState = (next: boolean) => {
        setOpen(next);
        onOpenChange?.(next);
    };

    return (
        <Autocomplete<SupplierNameOption, false, false, true>
            // The generic pins FreeSolo to `true`, so the prop takes `true | undefined`.
            freeSolo={allowAdd ? true : undefined}
            size="small"
            fullWidth
            disabled={disabled}
            open={open}
            onOpen={() => setOpenState(true)}
            onClose={() => setOpenState(false)}
            loading={loading}
            options={options}
            filterOptions={(candidates, params) => {
                // The search is server-side, so the page isn't filtered again — the only thing
                // added is the "+ Add" row for a name none of the candidates match.
                if (!allowAdd) return candidates;

                const query = params.inputValue.trim();
                const exists = candidates.some(c => c.name.toLowerCase() === query.toLowerCase());

                return query && !exists
                    ? [{entityId: null, name: query, isNew: true}, ...candidates]
                    : candidates;
            }}
            getOptionLabel={o => (typeof o === 'string' ? o : o.name)}
            isOptionEqualToValue={(o, v) => supplierNameOptionKey(o) === supplierNameOptionKey(v)}
            value={null}
            inputValue={inputValue}
            onInputChange={(_event, value, reason) => {
                // 'reset' fires when MUI echoes the (always null) value back — keep the caller's own text.
                if (reason === 'input') onInputChange(value);
                if (reason === 'clear') onInputChange('');
            }}
            onChange={(_event, value) => {
                if (value === null) {
                    onInputChange('');
                    return;
                }
                onSelect(typeof value === 'string' ? {entityId: null, name: value} : value);
            }}
            onBlur={onBlur}
            noOptionsText={loading ? 'Searching…' : emptyText}
            renderOption={({key, ...props}, option) => (
                <Stack
                    key={key}
                    component="li"
                    {...props}
                    direction="row"
                    alignItems="baseline"
                    gap={0.75}
                >
                    <Typography
                        component="span"
                        variant="body2"
                        {...(option.isNew ? {fontWeight: 600, color: COLORS.info.main} : {})}
                    >
                        {option.isNew ? `+ Add "${option.name}"` : option.name}
                    </Typography>
                    {option.hint && (
                        <Typography component="span" variant="caption" color="text.secondary">
                            {option.hint}
                        </Typography>
                    )}
                </Stack>
            )}
            renderInput={params => (
                <TextField
                    {...params}
                    variant={variant}
                    label={label}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    onKeyDown={e => {
                        if (e.key === 'Escape' && onEscape) {
                            e.preventDefault();
                            onEscape();
                        }
                    }}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {(loading || busy) && <CircularProgress size={16}/>}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
        />
    );
};
